import React, { useEffect, useMemo, useState } from "react";

// Fullscreen playful overlay shown in toddler mode when the kid taps "surprise".
// Pure CSS animations, no deps, no external assets. One of three variants is
// picked at random each time so it feels fresh: butterflies fluttering across
// the screen, bubbles drifting up, or balls bouncing.
//
// Dismisses on:
//   • any tap / click anywhere on the overlay
//   • Escape key (for a keyboard user)
//   • the auto-timeout (20s) so a toddler who walks away doesn't leave it running
//
// Every tap registers a "pop" — the tapped sprite disappears with a burst
// animation, which is very satisfying and keeps them engaged.

const VARIANTS = ["butterflies", "bubbles", "balls", "fish"];

const SPRITES = {
  butterflies: ["🦋", "🦋", "🦋", "🌸", "🌼"],
  bubbles: ["🫧", "🫧", "🫧", "🫧"],
  balls: ["⚽️", "🏀", "🎾", "🏐", "🎱"],
  fish: ["🐠", "🐟", "🐡", "🦑", "🐙", "🦀"],
};

export const FUN_VARIANTS = VARIANTS;

function randomSprites(variant, count) {
  const pool = SPRITES[variant];
  return Array.from({ length: count }, (_, i) => ({
    id: i,
    emoji: pool[Math.floor(Math.random() * pool.length)],
    leftPct: Math.random() * 92 + 2,   // 2–94% so sprites stay on-screen
    topPct: Math.random() * 80 + 8,    // 8–88%
    size: Math.floor(Math.random() * 40) + 48, // 48–88 px
    delay: Math.random() * 2,          // staggered entrance
    duration: 8 + Math.random() * 8,   // 8–16s loop
  }));
}

export default function FunScreen({ variant: variantProp, onClose }) {
  const variant = useMemo(() => {
    if (variantProp && VARIANTS.includes(variantProp)) return variantProp;
    return VARIANTS[Math.floor(Math.random() * VARIANTS.length)];
  }, [variantProp]);
  const [sprites, setSprites] = useState(() => randomSprites(variant, 18));

  // Auto-dismiss after 20 seconds — toddlers get distracted and wander off.
  useEffect(() => {
    const t = setTimeout(onClose, 20000);
    const onKey = (e) => e.key === "Escape" && onClose();
    window.addEventListener("keydown", onKey);
    return () => {
      clearTimeout(t);
      window.removeEventListener("keydown", onKey);
    };
  }, [onClose]);

  const pop = (id, e) => {
    // Only pop if they tapped a sprite; tapping the backdrop dismisses.
    e.stopPropagation();
    setSprites((s) => s.filter((x) => x.id !== id));
  };

  const animName =
    variant === "butterflies"
      ? "ff-fun-butterfly"
      : variant === "bubbles"
      ? "ff-fun-bubble"
      : variant === "fish"
      ? "ff-fun-fish"
      : "ff-fun-ball";

  // Each variant gets its own mood-matched gradient backdrop.
  const bg =
    variant === "fish"
      ? "radial-gradient(circle at 30% 20%, #BEE3F0 0%, #7EC0D6 50%, #3B87A5 100%)"
      : variant === "balls"
      ? "radial-gradient(circle at 30% 20%, #D8E9B8 0%, #A4C77A 50%, #6E9C4A 100%)"
      : variant === "bubbles"
      ? "radial-gradient(circle at 30% 20%, #FDE2EC 0%, #F7B5CE 50%, #E88AB0 100%)"
      : "radial-gradient(circle at 30% 20%, #FCE8CD 0%, #F4D6B2 40%, #E0C4A4 100%)";

  return (
    <div
      className="fixed inset-0 z-[60] overflow-hidden cursor-pointer select-none"
      style={{ background: bg }}
      onClick={onClose}
      data-testid="fun-screen"
      aria-label="Tap anywhere to close"
    >
      {sprites.map((s) => (
        <button
          key={s.id}
          type="button"
          onClick={(e) => pop(s.id, e)}
          className="absolute leading-none"
          style={{
            left: `${s.leftPct}%`,
            top: `${s.topPct}%`,
            fontSize: `${s.size}px`,
            animation: `${animName} ${s.duration}s ease-in-out ${s.delay}s infinite`,
            border: "none",
            background: "transparent",
            padding: 0,
            cursor: "pointer",
          }}
          aria-label="pop"
          data-testid={`fun-sprite-${s.id}`}
        >
          {s.emoji}
        </button>
      ))}
      <div
        className="absolute bottom-8 left-0 right-0 text-center label-tiny text-[color:var(--ink-2)] pointer-events-none"
        style={{ letterSpacing: "0.2em" }}
      >
        TAP ANYWHERE TO GO BACK
      </div>
    </div>
  );
}
